import { useMutation } from "@apollo/client";
import { StaticGoogleMap, Marker } from "react-static-google-map";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Branch } from "../../../../models/partner_model";
import { lang } from "../../../../middleware/lang";
import {
  EditBranchVariables,
  EditBranchResponse,
  EDIT_BRANCH,
  GET_BRANCHES,
  CreateBranchResponse,
  CreateBranchVariables,
  CREATE_BRANCH,
  DELETE_BRANCH,
  DeleteBranch,
  DeleteBranchVariables,
} from "../queries";
import GoogleMap from "./GoogleMap";
import { DeleteModal } from "../../../modals/deletes";

type Props = {
  id: string | undefined;
  branch: Branch | undefined;
};

const STATE_NAMES: Record<string, string> = {
  "1": "Aguascalientes", "2": "Baja California", "3": "Baja California Sur",
  "4": "Campeche", "5": "Coahuila", "6": "Colima", "7": "Chiapas",
  "8": "Chihuahua", "9": "Distrito Federal", "10": "Durango",
  "11": "Guanajuato", "12": "Guerrero", "13": "Hidalgo", "14": "Jalisco",
  "15": "México", "16": "Michoacán", "17": "Morelos", "18": "Nayarit",
  "19": "Nuevo León", "20": "Oaxaca", "21": "Puebla", "22": "Querétaro",
  "23": "Quintana Roo", "24": "San Luis Potosí", "25": "Sinaloa",
  "26": "Sonora", "27": "Tabasco", "28": "Tamaulipas", "29": "Tlaxcala",
  "30": "Veracruz", "31": "Yucatán", "32": "Zacatecas", "33": "Ciudad de México",
};

let mapsScriptPromise: Promise<void> | null = null;

const loadGoogleMapsScript = (): Promise<void> => {
  if ((window as any).google?.maps?.Geocoder) {
    return Promise.resolve();
  }
  if (!mapsScriptPromise) {
    mapsScriptPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById("google-maps-script");
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps script")));
        return;
      }
      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Maps script"));
      document.head.appendChild(script);
    });
  }
  return mapsScriptPromise;
};

const geocodeWithGoogleMaps = async (
  query: string,
): Promise<{ lat: number; lng: number } | null> => {
  try {
    await loadGoogleMapsScript();
  } catch {
    return null;
  }
  return new Promise((resolve) => {
    const geocoder = new (window as any).google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results: any[], status: string) => {
      if (status === "OK" && results.length > 0) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        console.error("Google Maps geocode failed", { status, query });
        resolve(null);
      }
    });
  });
};

const BranchGeneral: React.FC<Props> = ({ id, branch }) => {
  const history = useHistory();
  const isEdit = !!id;
  const [adjusting, setAdjusting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [location, setLocation] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [state, setState] = useState("0");
  const [colonia, setColonia] = useState("");
  const [contact, setContact] = useState("");
  const [privatePhone, setPrivatePhone] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(false);
  const [deleteBranch, { called: deleted, error: deleteError }] = useMutation<
    DeleteBranch,
    DeleteBranchVariables
  >(DELETE_BRANCH, {
    refetchQueries: [{ query: GET_BRANCHES }],
  });
  const [editBranch, { called, error }] = useMutation<
    EditBranchResponse,
    EditBranchVariables
  >(EDIT_BRANCH, {
    refetchQueries: [{ query: GET_BRANCHES }],
  });
  const [createBranch, { called: createCalled, error: createError }] =
    useMutation<CreateBranchResponse, CreateBranchVariables>(CREATE_BRANCH, {
      refetchQueries: [{ query: GET_BRANCHES }],
    });

  useEffect(() => {
    if (called && !error) {
      toast.success(lang("Sucursal actualizada", "Branch updated"));
      history.push("/branches");
    }
    if (createCalled && !createError) {
      toast.success(lang("Sucursal creada", "Branch created"));
      history.push("/branches");
    }
    if (deleted && !deleteError) {
      toast.success(lang("Sucursal eliminada", "Branch deleted"));
      history.push("/branches");
    }
  }, [history, called, error, createCalled, createError, deleted, deleteError]);

  useEffect(() => {
    if (!!branch) {
      const stringLocation = `${branch.location.latitude},${branch.location.longitude}`;
      const current = branch;
      setLocation(stringLocation);
      setName(current.name);
      setPhone(current.phone);
      setWhatsappPhone(current.whatsapp_phone || "");
      setZip(current.zip);
      setCity(current.city);
      setAddress(current.address);
      setState(current.state);
      setPrivatePhone(current.contact_phone);
      setContact(current.contact_name);
    }
  }, [branch]);

  const addressComplete = address.trim() && city.trim() && state !== "0";

  // Reset any stale geocode error once the address is edited again
  useEffect(() => {
    setGeocodeError(false);
  }, [address, city, state, colonia, zip]);

  const validateAddress = async () => {
    if (!addressComplete) return;
    const stateName = STATE_NAMES[state] || "";
    const parts = [address, colonia, city, stateName, zip, "Mexico"].filter(Boolean);
    const query = parts.join(", ");
    setGeocoding(true);
    setGeocodeError(false);
    const coords = await geocodeWithGoogleMaps(query);
    setGeocoding(false);
    if (coords) {
      setLocation(`${coords.lat},${coords.lng}`);
      setAdjusting(true);
    } else {
      setGeocodeError(true);
    }
  };

  const saveBranch = () => {
    if (!name.trim()) {
      toast.error(lang("El nombre es obligatorio", "Name is required"));
      return;
    }
    if (!location) {
      toast.error(lang("Debes seleccionar una ubicación en el mapa", "You must select a location on the map"));
      return;
    }
    const fullAddress = colonia ? `${address}, ${colonia}` : address;
    let data = {
      id: 0,
      name,
      phone,
      whatsapp_phone: whatsappPhone || null,
      address: fullAddress,
      city,
      zip,
      location,
      state: +state,
      contact_name: contact,
      contact_phone: privatePhone,
    };
    if (isEdit) {
      data.id = +id!;
      editBranch({ variables: { data } });
    } else {
      const { id, ...rest } = data;
      createBranch({ variables: { data: rest } });
    }
  };

  const sendDelete = () => {
    if (branch) {
      deleteBranch({ variables: { id: +branch.id } });
    }
  };

  const updateMarker = (coords: any) => {
    setLocation(`${coords.lat},${coords.lng}`);
    setAdjusting(false);
  };

  return (
    <>
      {/* Public info */}
      <div className="material_card p-3 mb-3">
        <h4>{lang("Datos Públicos", "Public Info")}</h4>
        <div className="form-group">
          <label htmlFor="branch_name">
            {lang("Nombre de la sucursal", "Branch name")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="branch_name"
            placeholder={lang("Sucursal Centro", "Downtown Branch")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <small className="form-text text-muted">
            {lang("Máximo 100 caracteres", "Max 100 characters")}
          </small>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_phone">{lang("Teléfono al público", "Public phone")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <small className="form-text text-muted">
                {lang("A qué número pueden marcar tus clientes?", "What number can your customers call?")}
              </small>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_whatsapp">
                <i className="fab fa-whatsapp" style={{ color: "#25D366", marginRight: "0.4rem" }}></i>
                WhatsApp
              </label>
              <input
                type="text"
                className="form-control"
                id="branch_whatsapp"
                placeholder="5216621234567"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
              />
              <small className="form-text text-muted">
                {lang(
                  "Incluir código de país, ej. 5216621234567",
                  "Include country code, e.g. 5216621234567"
                )}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="material_card p-3 mb-3">
        <h4>{lang("Ubicación", "Location")}</h4>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_zip">{lang("Código postal", "Zip code")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_zip"
                maxLength={5}
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
              />
              <small className="form-text text-muted">
                {lang(
                  "Ingresa estado, ciudad, colonia y código postal manualmente",
                  "Enter state, city, neighborhood and zip code manually"
                )}
              </small>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_state">{lang("Estado", "State")}</label>
              <select
                className="custom-select"
                id="branch_state"
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                <option value="0">-</option>
                <option value="1">Aguascalientes</option>
                <option value="2">Baja California</option>
                <option value="3">Baja California Sur</option>
                <option value="4">Campeche</option>
                <option value="5">Coahuila de Zaragoza</option>
                <option value="6">Colima</option>
                <option value="7">Chiapas</option>
                <option value="8">Chihuahua</option>
                <option value="9">Distrito Federal</option>
                <option value="10">Durango</option>
                <option value="11">Guanajuato</option>
                <option value="12">Guerrero</option>
                <option value="13">Hidalgo</option>
                <option value="14">Jalisco</option>
                <option value="15">México</option>
                <option value="16">Michoacán de Ocampo</option>
                <option value="17">Morelos</option>
                <option value="18">Nayarit</option>
                <option value="19">Nuevo León</option>
                <option value="20">Oaxaca</option>
                <option value="21">Puebla</option>
                <option value="22">Querétaro</option>
                <option value="23">Quintana Roo</option>
                <option value="24">San Luis Potosí</option>
                <option value="25">Sinaloa</option>
                <option value="26">Sonora</option>
                <option value="27">Tabasco</option>
                <option value="28">Tamaulipas</option>
                <option value="29">Tlaxcala</option>
                <option value="30">Veracruz de Ignacio de la Llave</option>
                <option value="31">Yucatán</option>
                <option value="32">Zacatecas</option>
                <option value="33">Ciudad de México</option>
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_city">{lang("Ciudad", "City")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_colonia">{lang("Colonia", "Neighborhood")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_colonia"
                value={colonia}
                onChange={(e) => setColonia(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="branch_address">{lang("Dirección", "Address")}</label>
          <input
            type="text"
            className="form-control"
            id="branch_address"
            placeholder={lang("Calle y número", "Street and number")}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <small className="form-text text-muted">
            {lang("Calle, número exterior e interior", "Street, exterior and interior number")}
          </small>
        </div>
        {!!location && !adjusting && (
          <>
            <StaticGoogleMap
              zoom={18}
              size="400x400"
              className="img-fluid"
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            >
              <Marker location={location} color="red" label="S" />
            </StaticGoogleMap>
            <button
              className="btn btn-outline-primary btn-sm mt-2"
              onClick={() => setAdjusting(true)}
            >
              <i className="fas fa-map-marker-alt"></i>{" "}
              {lang("Ajustar marcador", "Adjust marker")}
            </button>
          </>
        )}
        {!location && !geocoding && (
          <div className="alert alert-warning mt-2">
            <i className="fas fa-map-marker-alt mr-2"></i>
            {lang(
              "Completa la dirección, ciudad y estado, luego presiona \"Validar dirección\" para ubicar en el mapa.",
              "Fill in address, city and state, then press \"Validate address\" to locate on the map."
            )}
          </div>
        )}
        {!location && geocodeError && !geocoding && (
          <div className="alert alert-danger mt-2">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {lang(
              "No pudimos ubicar esta dirección. Verifica los datos o ajusta el marcador manualmente.",
              "We couldn't locate this address. Check the details or adjust the marker manually."
            )}
          </div>
        )}
        {!location && (
          <button
            className="btn btn-outline-primary btn-sm mt-2"
            type="button"
            disabled={!addressComplete || geocoding}
            onClick={validateAddress}
          >
            {geocoding ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {lang("Buscando ubicación...", "Locating address...")}
              </>
            ) : (
              <>
                <i className="fas fa-map-marker-alt mr-2"></i>
                {lang("Validar dirección", "Validate address")}
              </>
            )}
          </button>
        )}
        {!!location && adjusting && (
          <GoogleMap
            lat={+location.split(",")[0]}
            lng={+location.split(",")[1]}
            updateMarker={updateMarker}
            disabled={!addressComplete}
          />
        )}
      </div>

      {/* Private info */}
      <div className="material_card p-3 mb-3">
        <h4>
          {lang("Datos Privados", "Private Info")}{" "}
          <small className="text-muted">
            <i className="fas fa-exclamation-triangle"></i>{" "}
            {lang("Solo Foo puede ver y usar estos datos", "Only Foo can see and use this data")}
          </small>
        </h4>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_contact">{lang("Nombre de Contacto", "Contact Name")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
              <small className="form-text text-muted">
                {lang(
                  "Con quién nos podemos dirigir en caso de alguna duda o aclaración?",
                  "Who should we contact for questions or clarifications?"
                )}
              </small>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="contact_phone">{lang("Teléfono del Contacto", "Contact Phone")}</label>
              <input
                type="tel"
                className="form-control"
                id="contact_phone"
                value={privatePhone}
                onChange={(e) => setPrivatePhone(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex justify-content-between mt-3">
        {isEdit && (
          <button
            className="btn btn-outline-danger"
            type="button"
            onClick={() => setShowDelete(true)}
          >
            <i className="fa fa-trash"></i>{" "}
            {lang("Eliminar", "Delete")}
          </button>
        )}
        <button
          className="btn btn-primary"
          type="button"
          id="save_branch_btn"
          onClick={saveBranch}
        >
          <i className="far fa-save"></i>{" "}
          {lang("Guardar", "Save")}
        </button>
      </div>

      <DeleteModal
        open={showDelete}
        title={lang(
          `¿Eliminar sucursal "${name}"?`,
          `Delete branch "${name}"?`
        )}
        toggle={() => setShowDelete(false)}
        handleConfirm={sendDelete}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "Esta acción no se puede revertir",
            "This action cannot be undone"
          )}
        </p>
      </DeleteModal>
    </>
  );
};

export default BranchGeneral;
